import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const API_URL = "";

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, resend
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus('resend');
        setMessage('No verification token provided.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`, {
          method: 'POST'
        });

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred. Please try again.');
      }
    };

    verifyToken();
  }, [token]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification?email=${encodeURIComponent(resendEmail)}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Verification email sent!', {
          description: 'Please check your inbox for the verification link.'
        });
        setResendEmail('');
      } else {
        toast.error(data.detail || 'Failed to send verification email');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white py-16" data-testid="verify-email-page">
      {/* Transactional, one-time-link page — noindexed, no evergreen content. */}
      <SEOHead title="Verify Your Email" canonicalPath="/verify-email" noindex />
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="border-stone-200 shadow-lg">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 bg-cafa-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-cafa-red animate-spin" />
                </div>
                <h1 className="text-2xl font-serif text-stone-900 mb-4">Verifying Your Email</h1>
                <p className="text-stone-600">Please wait while we verify your email address...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-serif text-stone-900 mb-4">Email Verified!</h1>
                <p className="text-stone-600 mb-6">{message}</p>
                <p className="text-stone-500 mb-6">You now have full access to the Member's Area.</p>
                <Button 
                  onClick={() => navigate('/members/login')}
                  className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-2xl font-serif text-stone-900 mb-4">Verification Failed</h1>
                <p className="text-stone-600 mb-6">{message}</p>
                <p className="text-stone-500 mb-6">The verification link may have expired or is invalid.</p>
                
                {/* Resend form */}
                <div className="border-t border-stone-200 pt-6 mt-6">
                  <p className="text-sm text-stone-600 mb-4">Need a new verification link?</p>
                  <form onSubmit={handleResendVerification} className="space-y-4">
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="text-center"
                      data-testid="resend-email-input"
                    />
                    <Button 
                      type="submit"
                      disabled={resending}
                      className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                      data-testid="resend-verification-btn"
                    >
                      {resending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Resend Verification Email'
                      )}
                    </Button>
                  </form>
                </div>
              </>
            )}

            {status === 'resend' && (
              <>
                <div className="w-16 h-16 bg-cafa-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-cafa-red" />
                </div>
                <h1 className="text-2xl font-serif text-stone-900 mb-4">Email Verification</h1>
                <p className="text-stone-600 mb-6">{message}</p>
                <p className="text-stone-500 mb-6">Enter your email address to receive a new verification link.</p>
                
                <form onSubmit={handleResendVerification} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="text-center"
                    data-testid="resend-email-input"
                  />
                  <Button 
                    type="submit"
                    disabled={resending}
                    className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                    data-testid="resend-verification-btn"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Verification Email'
                    )}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-stone-600 hover:text-stone-900"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};
